/**
 * SyncService - Background synchronization service for offline operations
 *
 * Automatically retries queued operations when database becomes available.
 * Uses exponential backoff and removes operations after max retries.
 * Stops retrying on auth errors (401/403) to prevent infinite loops.
 */

import { OfflineQueue, QueuedOperation } from './OfflineQueue.ts';

type SyncCallback = (queueSize: number) => void;

/** HTTP status codes that should NOT be retried */
// #1481 — 400 ontbrak. Een clientfout levert bij een replay altijd dezelfde
// 400 op, dus bleef zo'n operatie drie retries lang in de wachtrij staan: met
// een sync-cyclus van 30 s en backoff minutenlang een zichtbare badge.
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403, 404, 409, 422]);

export class SyncService {
  private static syncInterval: number | null = null;
  private static SYNC_INTERVAL_MS = 30000; // 30 seconds
  private static BATCH_SIZE = 10; // Process max 10 operations per sync cycle
  private static isSyncing = false;
  private static listeners: Set<SyncCallback> = new Set();

  /**
   * Start background sync service
   */
  static start(): void {
    if (this.syncInterval) {
      return;
    }

    this.syncInterval = window.setInterval(() => {
      this.sync();
    }, this.SYNC_INTERVAL_MS);

    // Immediate first sync
    this.sync();
  }

  /**
   * Stop background sync service
   */
  static stop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Subscribe to queue size changes
   */
  static subscribe(callback: SyncCallback): () => void {
    this.listeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of queue size change
   */
  private static notifyListeners(): void {
    const size = OfflineQueue.size();
    this.listeners.forEach((callback) => {
      try {
        callback(size);
      } catch (error) {
        console.error('Error in SyncService listener:', error);
      }
    });
  }

  /**
   * Manual sync trigger (useful for testing)
   */
  static async triggerSync(): Promise<void> {
    return this.sync();
  }

  /**
   * Main sync logic - replays queued operations in batches
   */
  private static async sync(): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    this.isSyncing = true;

    try {
      const queue = OfflineQueue.getQueue();
      if (queue.length === 0) {
        return;
      }

      // Check if database is reachable
      const isOnline = await this.checkDatabaseHealth();
      if (!isOnline) {
        return;
      }

      // Process in batches to prevent long-running sync cycles
      const batch = queue.slice(0, this.BATCH_SIZE);
      let successCount = 0;
      let failCount = 0;
      let authFailure = false;

      for (const op of batch) {
        // Stop entire sync cycle on auth failure — token expired, no point continuing
        if (authFailure) break;

        try {
          await this.replayOperation(op);
          OfflineQueue.dequeue(op.id);
          successCount++;
        } catch (error) {
          const status = (error as any)?.status;

          if (NON_RETRYABLE_STATUSES.has(status)) {
            // Auth/client errors: remove immediately, retrying won't help
            console.warn(`Operation ${op.id} returned ${status} — removing (non-retryable)`);
            OfflineQueue.dequeue(op.id);
            failCount++;

            if (status === 401 || status === 403) {
              authFailure = true;
            }
            continue;
          }

          console.error(`Failed to sync operation ${op.id}:`, error);

          // Increment retry count for server errors
          OfflineQueue.incrementRetry(op.id);
          const updatedOp = OfflineQueue.getQueue().find((o) => o.id === op.id);

          if (updatedOp && updatedOp.retryCount >= updatedOp.maxRetries) {
            console.error(
              `Max retries (${updatedOp.maxRetries}) reached for operation ${op.id}, removing from queue`
            );
            OfflineQueue.dequeue(op.id);
            failCount++;
          }

          // Add exponential backoff delay before next operation
          await new Promise((resolve) =>
            setTimeout(resolve, Math.min(1000 * Math.pow(2, op.retryCount), 10000))
          );
        }
      }

      // Dispatch sync result events for UI feedback
      if (successCount > 0) {
        window.dispatchEvent(
          new CustomEvent('pt-sync-complete', { detail: { count: successCount } })
        );
      }
      if (failCount > 0) {
        console.warn(`${failCount} operation(s) failed permanently`);
        window.dispatchEvent(new CustomEvent('pt-sync-failed', { detail: { count: failCount } }));
      }

      // Notify listeners after sync attempt
      this.notifyListeners();
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Check if database is healthy and reachable
   */
  private static async checkDatabaseHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/health', {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Replay a single queued operation
   * Throws error with status property for non-retryable detection
   */
  private static async replayOperation(op: QueuedOperation): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(op.endpoint, {
        method: op.method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(op.data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        const error: any = new Error(`Sync failed [${response.status}]: ${errorText}`);
        error.status = response.status;
        throw error;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Get current sync status
   */
  static getStatus(): { isRunning: boolean; isSyncing: boolean; queueSize: number } {
    return {
      isRunning: this.syncInterval !== null,
      isSyncing: this.isSyncing,
      queueSize: OfflineQueue.size(),
    };
  }
}
